/**
 * JiraConnectionPrompt Component
 * Task 7.4.7: Jira Not Connected State (FR-7, FR-19)
 *
 * Inline prompt shown when Jira is not connected.
 * Different behaviors for admins (can connect) vs non-admins (see contact message).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { tokens } from '../../styles/tokens';

export interface JiraConnectionPromptProps {
  /** Whether the current user is a site admin */
  isAdmin: boolean;
  /** URL to site settings/integrations page */
  settingsUrl?: string;
  /** Callback when connect button is clicked (overrides default navigation) */
  onConnect?: () => void;
  /** Callback when prompt is closed */
  onClose: () => void;
}

// Jira icon SVG component
const JiraIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = '#0052CC',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12.006 1.5L3.99 9.516c-.915.915-.915 2.397 0 3.312L7.299 16.137l4.707-4.707 4.707 4.707 3.309-3.309c.915-.915.915-2.397 0-3.312L12.006 1.5z"
      fill={color}
    />
    <path
      d="M12.006 13.431l-4.707 4.707L12 22.839l4.713-4.714-4.707-4.694z"
      fill={color}
    />
  </svg>
);

export const JiraConnectionPrompt: React.FC<JiraConnectionPromptProps> = ({
  isAdmin,
  settingsUrl,
  onConnect,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  // Handle connect button click
  const handleConnect = useCallback(() => {
    if (onConnect) {
      onConnect();
    } else if (settingsUrl) {
      window.open(settingsUrl, '_blank', 'noopener,noreferrer');
    }
  }, [onConnect, settingsUrl]);

  // Scroll into view and focus when opened
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      containerRef.current.focus();
    }
  }, []);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Connect Jira to create tickets"
      aria-modal="false"
      tabIndex={-1}
      style={containerStyle}
      onKeyDown={handleKeyDown}
      data-testid="jira-connection-prompt"
    >
      {/* Header with icon */}
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          <JiraIcon size={32} />
        </div>
        <button
          type="button"
          onClick={onClose}
          style={closeButtonStyle}
          aria-label="Close"
          data-testid="jira-connection-close"
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4l8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Message */}
      <div style={contentStyle}>
        <h3 style={headingStyle}>Connect Jira</h3>
        <p style={messageStyle}>
          Connect Jira to create tickets from comments
        </p>
      </div>

      {/* Actions */}
      <div style={actionsStyle}>
        {isAdmin ? (
          <button
            type="button"
            onClick={handleConnect}
            style={connectButtonStyle}
            data-testid="jira-connect-button"
          >
            Connect Jira
          </button>
        ) : (
          <p style={adminNoteStyle}>
            Contact your site admin to connect Jira
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Styles using PDS Tokens
// ============================================================================

const containerStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.bgSecondary,
  border: `1px solid ${tokens.colors.borderDefault}`,
  borderRadius: '8px',
  padding: '16px',
  marginTop: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const iconContainerStyle: React.CSSProperties = {
  width: '48px',
  height: '48px',
  backgroundColor: tokens.colors.bgDefault,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${tokens.colors.borderDefault}`,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  color: tokens.colors.textSecondary,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const headingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
  color: tokens.colors.textDefault,
};

const messageStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '13px',
  color: tokens.colors.textSecondary,
  lineHeight: 1.4,
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const connectButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: tokens.colors.buttonPrimaryFg,
  backgroundColor: tokens.colors.buttonPrimaryBg,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  width: '100%',
};

const adminNoteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '12px',
  color: tokens.colors.textSecondary,
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '8px 0',
};

export default JiraConnectionPrompt;
